/* Les prix, modifiables, et leur histoire.
 *
 * Trois onglets, un par prix : ce que l'usine demande, ce qu'un agent n'a pas
 * le droit de franchir, ce qu'un client paie. Chacun s'édite en place, et
 * chaque référence ouvre son historique daté — qui l'a posé, quand, pourquoi.
 *
 * On ne modifie pas un prix : on en pose un nouveau. L'ancien se ferme, il ne
 * s'efface pas. C'est tout l'intérêt.
 */

import { useMemo, useState } from "react";
import {
  useAgents, useClients, usePrixAgent, usePrixClient, usePrixUsine, useProduits, useUsines,
} from "@/lib/donnees";
import { useNiveau } from "@/lib/session";
import { historique, margeDe } from "@/domain/prix";
import { Empty, Kpi, Kpis, Onglets, Section, Tag } from "@/ui/primitives";
import { Drawer } from "@/ui/Drawer";
import { Table } from "@/ui/Table";
import type { Colonne } from "@/ui/Table";
import { useToast } from "@/ui/Toast";
import { dateFr, entier, euro } from "@/lib/format";
import { poserPlancherAgent, poserPrixUsine, poserTarifClient } from "./actions";
import type { Produit } from "@/types/metier";

type Onglet = "usine" | "agent" | "client";

interface Ligne {
  produit: Produit;
  /** Le prix en vigueur pour la colonne choisie, s'il y en a un. */
  valeur: number | null;
  /** Ce à quoi il se compare : prix usine pour un plancher, plancher pour un tarif. */
  repere: number | null;
}

export function Prix() {
  const niveau = useNiveau();
  const toast = useToast();
  const [onglet, setOnglet] = useState<Onglet>("usine");
  const [agentId, setAgentId] = useState("");
  const [clientId, setClientId] = useState("");
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const { donnees: produits } = useProduits();
  const { donnees: usines } = useUsines();
  const { donnees: agents } = useAgents();
  const { donnees: clients } = useClients();
  const { donnees: pUsine, recharger: rU } = usePrixUsine(true);
  const { donnees: pAgent, recharger: rA } = usePrixAgent(true);
  const { donnees: pClient, recharger: rC } = usePrixClient(true);

  const nomUsine = useMemo(() => new Map(usines.map((u) => [u.id, u.nom] as const)), [usines]);
  const nomAgent = useMemo(
    () => new Map(agents.map((a) => [a.id, a.nom] as const)),
    [agents],
  );

  if (niveau !== "admin") {
    return (
      <div className="pad">
        <Empty
          titre="Réservé à la direction"
          detail="Les prix se posent depuis un compte de direction. Votre plancher reste visible au catalogue."
        />
      </div>
    );
  }

  const courantUsine = new Map(
    pUsine.filter((l) => !l.fin_le).map((l) => [l.produit_id, Number(l.prix)] as const),
  );
  const courantAgent = new Map(
    pAgent.filter((l) => !l.fin_le && l.agent_id === agentId)
      .map((l) => [l.produit_id, Number(l.prix_mini)] as const),
  );
  const courantClient = new Map(
    pClient.filter((l) => !l.fin_le && l.client_id === clientId)
      .map((l) => [l.produit_id, Number(l.prix)] as const),
  );

  /* Le plancher de référence d'un agent : sa ligne à lui, sinon le prix du
     produit. C'est ce à quoi un tarif client se compare. */
  const plancher = (p: Produit) => courantAgent.get(p.id) ?? (p.prix ?? null);

  const lignes: Ligne[] = produits.map((p) => {
    if (onglet === "usine") {
      return { produit: p, valeur: courantUsine.get(p.id) ?? null, repere: p.prix ?? null };
    }
    if (onglet === "agent") {
      return { produit: p, valeur: courantAgent.get(p.id) ?? null, repere: courantUsine.get(p.id) ?? null };
    }
    return { produit: p, valeur: courantClient.get(p.id) ?? null, repere: plancher(p) };
  });

  const cible = onglet === "agent" ? agentId : onglet === "client" ? clientId : "x";
  const poses = lignes.filter((l) => l.valeur !== null).length;

  async function poser(p: Produit, brut: string) {
    const prix = Number(brut.replace(",", "."));
    if (!Number.isFinite(prix) || prix < 0) return;
    setOccupe(true);
    try {
      if (onglet === "usine") await poserPrixUsine(p.id, prix, "saisie");
      else if (onglet === "agent") await poserPlancherAgent(agentId, p.id, prix, "saisie");
      else await poserTarifClient(clientId, p.id, prix, "saisie");
      toast(`${p.ref} · ${euro(prix)}`);
      if (onglet === "usine") rU();
      else if (onglet === "agent") rA();
      else rC();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Le prix n'a pas été posé.");
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: (Colonne<Ligne> | false)[] = [
    { cle: "ref", titre: "Référence", type: "ref", filtre: "texte",
      texte: (l) => l.produit.ref, sous: (l) => l.produit.nom },
    { cle: "nom", titre: "Désignation", classe: "name", mobile: false, filtre: "texte",
      texte: (l) => l.produit.nom },
    { cle: "usine", titre: "Usine", mobile: false, filtre: "select",
      texte: (l) => nomUsine.get(l.produit.usine_id) ?? "—" },
    {
      cle: "repere",
      titre: onglet === "usine" ? "Prix de référence" : onglet === "agent" ? "Prix usine" : "Plancher",
      court: "Repère",
      type: "euro",
      mobile: false,
      nombre: (l) => l.repere ?? 0,
      rendu: (l) => (l.repere == null ? <span className="locked">—</span> : euro(l.repere)),
    },
    {
      cle: "valeur",
      titre: onglet === "usine" ? "Prix usine" : onglet === "agent" ? "Plancher" : "Tarif client",
      court: "Prix",
      type: "euro",
      nombre: (l) => l.valeur ?? 0,
      rendu: (l) => (
        <input
          type="number"
          step="0.0001"
          min="0"
          className="prix-edit"
          aria-label={`Prix pour ${l.produit.ref}`}
          defaultValue={l.valeur ?? ""}
          disabled={occupe || !cible}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (!v || Number(v) === l.valeur) return;
            void poser(l.produit, v);
          }}
        />
      ),
    },
    onglet !== "usine" && {
      cle: "ecart",
      titre: "Écart",
      type: "euro",
      mobile: false,
      nombre: (l) => margeDe(l.valeur, l.repere) ?? 0,
      rendu: (l) => {
        const m = margeDe(l.valeur, l.repere);
        if (m === null) return <span className="locked">—</span>;
        return <span className={m < 0 ? "marge-ko" : ""}>{euro(m)}</span>;
      },
    },
    {
      cle: "histoire",
      titre: "",
      court: "",
      mobile: false,
      rendu: (l) => (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOuvert(l.produit.id)}>
          Historique
        </button>
      ),
    },
  ];

  const archives = ouvert ? archivesDe(ouvert) : [];

  function archivesDe(produitId: string) {
    if (onglet === "usine") {
      return historique(pUsine.filter((l) => l.produit_id === produitId))
        .map((l) => ({ ...l, prix: Number(l.prix) }));
    }
    if (onglet === "agent") {
      return historique(pAgent.filter((l) => l.produit_id === produitId && l.agent_id === agentId))
        .map((l) => ({ ...l, prix: Number(l.prix_mini) }));
    }
    return historique(pClient.filter((l) => l.produit_id === produitId && l.client_id === clientId))
      .map((l) => ({ ...l, prix: Number(l.prix) }));
  }

  const produitOuvert = produits.find((p) => p.id === ouvert);

  return (
    <div className="pad">
      <div className="barre-onglets">
        <Onglets
          items={[
            { cle: "usine", nom: "Prix usine" },
            { cle: "agent", nom: "Planchers agents" },
            { cle: "client", nom: "Tarifs clients" },
          ]}
          actif={onglet}
          onChange={(c) => {
            setOnglet(c as Onglet);
            setOuvert(null);
          }}
        />
      </div>

      {onglet === "agent" ? (
        <Choix
          id="prix-agent"
          label="Agent"
          valeur={agentId}
          onChange={setAgentId}
          options={agents.map((a) => ({ cle: a.id, nom: `${a.nom} — ${a.email}` }))}
          vide="Choisissez un agent"
        />
      ) : null}

      {onglet === "client" ? (
        <Choix
          id="prix-client"
          label="Client"
          valeur={clientId}
          onChange={setClientId}
          options={clients.map((c) => ({ cle: c.id, nom: c.nom }))}
          vide="Choisissez un client"
        />
      ) : null}

      {!cible ? (
        <Empty
          titre={onglet === "agent" ? "Aucun agent choisi" : "Aucun client choisi"}
          detail="Un plancher et un tarif se posent pour quelqu'un : choisissez-le au-dessus."
        />
      ) : (
        <>
          <Kpis>
            <Kpi k="Références" v={entier(produits.length)} n={`${usines.length} usines`} />
            <Kpi k="Prix posés" v={entier(poses)} n={`sur ${entier(produits.length)} références`} />
            <Kpi
              k="Sans prix"
              v={entier(produits.length - poses)}
              n={onglet === "client" ? "le plancher s'applique" : "rien de posé"}
            />
          </Kpis>

          <Section
            titre={
              onglet === "usine" ? "Ce que l'usine demande"
                : onglet === "agent" ? "Ce que l'agent ne peut pas franchir"
                : "Ce que ce client paie"
            }
            note="un prix saisi ferme le précédent, il ne l'efface pas"
          >
            <Table
              lignes={lignes}
              cle={(l) => l.produit.id}
              colonnes={colonnes}
              chercher="Référence ou désignation"
            />
          </Section>
        </>
      )}

      {ouvert && produitOuvert ? (
        <Drawer
          titre={produitOuvert.ref}
          sous={produitOuvert.nom}
          onClose={() => setOuvert(null)}
        >
          {archives.length ? (
            <div className="rows">
              {archives.map((a) => (
                <div className="row" key={a.id}>
                  <div className={`row-rail${a.fin_le ? "" : " ok"}`} />
                  <div className="row-body">
                    <div className="row-t">
                      <span className="mono">{euro(a.prix)}</span>
                      {a.fin_le ? null : <Tag ton="ok" dot>en vigueur</Tag>}
                    </div>
                    <div className="row-meta">
                      du {dateFr(a.debut_le)}
                      {a.fin_le ? ` au ${dateFr(a.fin_le)}` : ""}
                      {a.pose_par ? ` · ${nomAgent.get(a.pose_par) ?? "—"}` : ""}
                      {a.motif ? ` · ${a.motif}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty titre="Aucun prix posé" detail="Cette référence n'a pas encore d'histoire ici." />
          )}
        </Drawer>
      ) : null}
    </div>
  );
}

/** Une liste déroulante étiquetée, dans la barre de filtres. */
function Choix({
  id, label, valeur, onChange, options, vide,
}: {
  id: string;
  label: string;
  valeur: string;
  onChange: (v: string) => void;
  options: { cle: string; nom: string }[];
  vide: string;
}) {
  return (
    <div className="filterbar">
      <div className="champ">
        <label htmlFor={id}>{label}</label>
        <select id={id} value={valeur} onChange={(e) => onChange(e.target.value)}>
          <option value="">{vide}</option>
          {options.map((o) => (
            <option key={o.cle} value={o.cle}>{o.nom}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
